import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { MessageService } from 'primeng/api';
import { TierUpgradeService, TierLimitContext } from '../services/tier-upgrade.service';
import { EnterpriseRequestService } from '../services/enterprise-request.service';

/**
 * Pricing Error Codes from backend
 */
export const PRICING_ERROR_CODES = {
  TIER_LIMIT_EXCEEDED: 'TIER_LIMIT_EXCEEDED',
  LIMIT_REACHED: 'LIMIT_REACHED',
  FEATURE_NOT_AVAILABLE: 'FEATURE_NOT_AVAILABLE',
  INSUFFICIENT_CREDITS: 'INSUFFICIENT_CREDITS',
  NO_SUBSCRIPTION: 'NO_SUBSCRIPTION',
  SUBSCRIPTION_INACTIVE: 'SUBSCRIPTION_INACTIVE',
  SUBSCRIPTION_CANCELED: 'SUBSCRIPTION_CANCELED',
  PAYMENT_REQUIRED: 'PAYMENT_REQUIRED',
  PAYMENT_PAST_DUE: 'PAYMENT_PAST_DUE',
  RATE_LIMIT_EXCEEDED: 'RATE_LIMIT_EXCEEDED'
} as const;

export type PricingErrorCode = typeof PRICING_ERROR_CODES[keyof typeof PRICING_ERROR_CODES];

export interface UpgradeTier {
  tierId: string;
  name: string;
  limit: number;    // limit for the specific metric (-1 = unlimited)
  price: number;    // monthly price
  agencyPricing?: {
    childrenAllowed: boolean;
    perChildMonthlyFee: number;
    maxChildren: number;
  };
}

export interface PricingError {
  error: PricingErrorCode;
  message: string;
  upgradeRequired?: boolean;
  purchaseRequired?: boolean;
  subscriptionRequired?: boolean;
  reactivationRequired?: boolean;
  paymentRequired?: boolean;
  current?: number;
  limit?: number;
  remaining?: number;
  needed?: number;
  shortfall?: number;
  feature?: string;
  resetAt?: string;
  metric?: string;                  // e.g. 'integrations', 'sequences', 'teamMembers'
  upgradeTiers?: UpgradeTier[];     // tiers that would accommodate the limit
  isEnterprise?: boolean;           // true if user is on enterprise tier (from backend)
}

/**
 * HTTP Interceptor to handle pricing/tier-related API errors
 * Shows user-friendly messages and optionally redirects to billing page
 */
export const pricingErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const messageService = inject(MessageService);
  const tierUpgradeService = inject(TierUpgradeService);
  const enterpriseRequestService = inject(EnterpriseRequestService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Only handle pricing-related errors
      if (isPricingError(error)) {
        handlePricingError(error, router, messageService, tierUpgradeService, enterpriseRequestService);
      }

      return throwError(() => error);
    })
  );
};

/**
 * Check if error is a pricing-related error
 */
function isPricingError(error: HttpErrorResponse): boolean {
  if (!error.error?.error) return false;

  const errorCode = error.error.error;
  return Object.values(PRICING_ERROR_CODES).includes(errorCode);
}

/**
 * Handle pricing error with appropriate UI feedback
 */
function handlePricingError(
  error: HttpErrorResponse,
  router: Router,
  messageService: MessageService,
  tierUpgradeService: TierUpgradeService,
  enterpriseRequestService: EnterpriseRequestService
): void {
  const pricingError = error.error as PricingError;
  const errorCode = pricingError.error;

  switch (errorCode) {
    case PRICING_ERROR_CODES.TIER_LIMIT_EXCEEDED:
    case PRICING_ERROR_CODES.LIMIT_REACHED:
      showLimitExceededMessage(pricingError, router, messageService, tierUpgradeService, enterpriseRequestService);
      break;

    case PRICING_ERROR_CODES.FEATURE_NOT_AVAILABLE:
      showFeatureNotAvailableMessage(pricingError, router, messageService);
      break;

    case PRICING_ERROR_CODES.INSUFFICIENT_CREDITS:
      showInsufficientCreditsMessage(pricingError, router, messageService);
      break;

    case PRICING_ERROR_CODES.NO_SUBSCRIPTION:
    case PRICING_ERROR_CODES.SUBSCRIPTION_INACTIVE:
    case PRICING_ERROR_CODES.SUBSCRIPTION_CANCELED:
      showSubscriptionRequiredMessage(pricingError, router, messageService);
      break;

    case PRICING_ERROR_CODES.PAYMENT_REQUIRED:
    case PRICING_ERROR_CODES.PAYMENT_PAST_DUE:
      showPaymentRequiredMessage(pricingError, router, messageService);
      break;

    case PRICING_ERROR_CODES.RATE_LIMIT_EXCEEDED:
      showRateLimitMessage(pricingError, messageService);
      break;

    default:
      showGenericPricingError(pricingError, messageService);
  }
}

function showLimitExceededMessage(
  error: PricingError,
  router: Router,
  messageService: MessageService,
  tierUpgradeService: TierUpgradeService,
  enterpriseRequestService: EnterpriseRequestService
): void {
  // Store upgrade context for billing page (always, for both dialog and toast paths)
  sessionStorage.setItem('upgradeContext', JSON.stringify({
    reason: 'limit_exceeded',
    metric: error.metric,
    current: error.current,
    limit: error.limit,
    upgradeTiers: error.upgradeTiers,
    upgradeRequired: error.upgradeRequired,
    timestamp: Date.now()
  }));

  // If we have upgrade tier data (non-enterprise users), show the upgrade dialog.
  // Use setTimeout to allow the calling component's catch block to close its own dialog first,
  // preventing z-index stacking issues with PrimeNG DynamicDialogs.
  if (error.upgradeTiers && error.upgradeTiers.length > 0) {
    const upgradeTiers = error.upgradeTiers;
    setTimeout(() => {
      tierUpgradeService.showUpgradeDialog({
        metric: error.metric || 'resource',
        currentUsage: error.current || 0,
        limit: error.limit || 0,
        upgradeTiers,
        message: error.message
      });
    }, 300);
  } else if (error.isEnterprise) {
    // Enterprise customer hit a custom limit — open enterprise request dialog
    // to request a limit increase
    setTimeout(() => {
      enterpriseRequestService.openRequestDialog({
        metric: error.metric,
        currentUsage: error.current,
        limit: error.limit
      });
    }, 300);
  } else {
    // Legacy fallback: show toast when no upgrade tier data available
    showLimitExceededToast(error, messageService);
  }
}

function showLimitExceededToast(
  error: PricingError,
  messageService: MessageService
): void {
  messageService.add({
    severity: 'warn',
    summary: 'Limit Reached',
    detail: error.message || `You've reached your plan limit (${error.current}/${error.limit}).`,
    life: 8000,
    key: 'pricing-toast'
  });
}

function showFeatureNotAvailableMessage(
  error: PricingError,
  router: Router,
  messageService: MessageService
): void {
  messageService.add({
    severity: 'info',
    summary: 'Feature Not Available',
    detail: error.message || `The ${error.feature || 'requested'} feature requires a higher tier plan.`,
    life: 8000,
    key: 'pricing-toast'
  });

  sessionStorage.setItem('upgradeContext', JSON.stringify({
    reason: 'feature_not_available',
    feature: error.feature
  }));
}

function showInsufficientCreditsMessage(
  error: PricingError,
  router: Router,
  messageService: MessageService
): void {
  const needed = error.needed || 1;
  const remaining = error.remaining ?? 0;

  messageService.add({
    severity: 'warn',
    summary: 'Insufficient AI Credits',
    detail: `You need ${needed} credit${needed !== 1 ? 's' : ''} but only have ${remaining}. Redirecting to billing to purchase more...`,
    life: 5000,
    key: 'pricing-toast'
  });

  sessionStorage.setItem('upgradeContext', JSON.stringify({
    reason: 'insufficient_credits',
    needed: error.needed,
    remaining: error.remaining,
    shortfall: error.shortfall
  }));

  // Auto-navigate to billing page after a short delay
  setTimeout(() => {
    router.navigate(['/billing']);
  }, 3000);
}

function showSubscriptionRequiredMessage(
  error: PricingError,
  router: Router,
  messageService: MessageService
): void {
  const detail = error.error === PRICING_ERROR_CODES.SUBSCRIPTION_CANCELED
    ? 'Your subscription has been canceled. Reactivate to continue using premium features.'
    : 'An active subscription is required to access this feature.';

  messageService.add({
    severity: 'error',
    summary: 'Subscription Required',
    detail,
    life: 10000,
    key: 'pricing-toast'
  });

  // Auto-redirect to billing after a delay
  setTimeout(() => {
    router.navigate(['/billing']);
  }, 3000);
}

function showPaymentRequiredMessage(
  error: PricingError,
  router: Router,
  messageService: MessageService
): void {
  messageService.add({
    severity: 'error',
    summary: 'Payment Required',
    detail: 'Your payment is past due. Please update your payment method to continue.',
    life: 10000,
    key: 'pricing-toast'
  });

  // Auto-redirect to billing
  setTimeout(() => {
    router.navigate(['/billing']);
  }, 3000);
}

function showRateLimitMessage(
  error: PricingError,
  messageService: MessageService
): void {
  const resetTime = error.resetAt
    ? new Date(error.resetAt).toLocaleTimeString()
    : 'midnight';

  messageService.add({
    severity: 'warn',
    summary: 'Rate Limit Exceeded',
    detail: `You've exceeded your daily API limit. Limits reset at ${resetTime}.`,
    life: 8000,
    key: 'pricing-toast'
  });
}

function showGenericPricingError(
  error: PricingError,
  messageService: MessageService
): void {
  messageService.add({
    severity: 'error',
    summary: 'Error',
    detail: error.message || 'A pricing-related error occurred.',
    life: 5000,
    key: 'pricing-toast'
  });
}

/**
 * Helper to check if an error response is a specific pricing error
 */
export function isPricingErrorCode(
  error: HttpErrorResponse,
  code: PricingErrorCode
): boolean {
  return error.error?.error === code;
}

/**
 * Helper to get upgrade URL with context
 */
export function getUpgradeUrl(context?: Record<string, unknown>): string {
  if (context) {
    sessionStorage.setItem('upgradeContext', JSON.stringify(context));
  }
  return '/billing';
}
